"use client";

/**
 * Context provider for match page data.
 *
 * Provides computed match data and utilities to all child components,
 * complementing the MatchProvider from fansunited-data-layer.
 */
import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useMemo } from "react";
import type { FUSportsH2HSummary, FUSportsMatchMember, FUSportsTeamCompetitor } from "fansunited-data-layer";
import type { MarketOddsMap } from "@/features/odds/types";
import { useMatchOdds, DEFAULT_ODDS_POLLING_INTERVAL_MS } from "@/features/odds/useMatchOdds";
import { MATCH_PAGE_ODDS_MARKETS } from "@/features/odds/matchPageMarkets";
import { useMatch } from "fansunited-data-layer/client";
import { COMPETITORS } from "@/config/constants";
import { mergeTeamData } from "@/utilities/mappers/team";
import type { MatchNewsResult } from "@/utilities/data/getMatchNews";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
export interface MatchPageDataContextValue {
  /** The match with native ID */
  match: FUSportsMatchWithNativeId;
  /** Home team competitor with merged branding data */
  homeTeam: FUSportsTeamCompetitor;
  /** Away team competitor with merged branding data */
  awayTeam: FUSportsTeamCompetitor;
  /** Whether the match is currently live */
  isLive: boolean;
  /** Whether the match has finished */
  isFinished: boolean;
  /** Whether lineups are available */
  hasLineups: boolean;
  /** Whether events are available */
  hasEvents: boolean;
  /** Odds data map */
  oddsMap: MarketOddsMap;
  /** Whether odds are loading */
  oddsLoading: boolean;
  /** Function to refetch odds */
  refetchOdds: () => void;
  /** Native (FansUnited) ID for the home team */
  homeTeamNativeId: string;
  /** Native (FansUnited) ID for the away team */
  awayTeamNativeId: string;
  /** Map of Sportal365 player IDs to native FansUnited IDs for player page links */
  playerIdMap: Record<string, string>;
  /** News articles related to the match and teams (SSR data) */
  news: MatchNewsResult | null;
  /** TV broadcaster listings for this match in the current tenant (SSR data) */
  tvListings: ResolvedListings;
  /** Explicit winner block from the Sports Events API (`{competitorId, reason}`); undefined for not-started / in-progress / drawn fixtures. */
  winner: {
    competitorId: string;
    reason: string;
  } | undefined;
  /** Pre-computed H2H summary metrics from the Sports Events API; `[]` when the API doesn't ship summary. */
  h2hSummary: FUSportsH2HSummary[];
  /** Match referees resolved via the hydrated entities map; undefined when none shipped. */
  referees: FUSportsMatchMember[] | undefined;
  /** Live clock state (`playedSeconds` + `clockStatus`); undefined on records without clock state. */
  liveClock: {
    playedSeconds: number;
    clockStatus: string;
  } | undefined;
}
const MatchPageDataContext = createContext<MatchPageDataContextValue | null>(null);
interface MatchPageDataProviderProps {
  children: React.ReactNode;
  /** Native (FansUnited) ID for the home team, resolved server-side */
  homeTeamNativeId?: string;
  /** Native (FansUnited) ID for the away team, resolved server-side */
  awayTeamNativeId?: string;
  /** Map of Sportal365 player IDs to native FansUnited IDs */
  playerIdMap?: Record<string, string>;
  /** News data fetched server-side for SSR */
  news?: MatchNewsResult | null;
  /** TV listings resolved server-side for SSR */
  tvListings?: ResolvedListings;
  winner?: {
    competitorId: string;
    reason: string;
  };
  h2hSummary?: FUSportsH2HSummary[];
  referees?: FUSportsMatchMember[];
  liveClock?: {
    playedSeconds: number;
    clockStatus: string;
  };
}

/**
 * Provider component that adds page-level computed values on top of MatchProvider.
 * Must be used within a MatchProvider.
 */
// Fallback for the (rare) case where TV listings data wasn't threaded through — mirrors
// `emptyListings` but with no locale/tenant context available at this level. Server-side
// callers should always pass `tvListings` explicitly; this is a safety net only.
const EMPTY_TV_LISTINGS: ResolvedListings = {
  defaultCountry: "GB",
  defaultCountryListings: [],
  byCountry: {}
};
export function MatchPageDataProvider(t0) {
  const $ = _c(39);
  const {
    children,
    homeTeamNativeId: homeTeamNativeIdProp,
    awayTeamNativeId: awayTeamNativeIdProp,
    playerIdMap: t1,
    news: t2,
    tvListings: t3,
    winner,
    h2hSummary: t4,
    referees,
    liveClock
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const playerIdMapProp = t5;
  const news = t2 === undefined ? null : t2;
  const tvListings = t3 === undefined ? EMPTY_TV_LISTINGS : t3;
  let t6;
  if ($[2] !== t4) {
    t6 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const h2hSummary = t6;
  const {
    match: rawMatch,
    lineups,
    events
  } = useMatch();
  const match = rawMatch as FUSportsMatchWithNativeId;
  const homeTeamNativeId = homeTeamNativeIdProp || match.competitorOne.id;
  const awayTeamNativeId = awayTeamNativeIdProp || match.competitorTwo.id;
  const statusType = match.status?.type || "";
  const statusCode = (match.status?.code || "") as string;
  const isLive = statusType === "LIVE" || statusCode === "live" || statusCode === "in_progress";
  const isFinished = statusType === "FINISHED";
  const hasLineups = Boolean(lineups && lineups.confirmed);
  const hasEvents = Boolean(events && events.length > 0);
  const matchIdForOdds = match.fansUnitedMatchId || match.id;
  const t7 = !isFinished ? matchIdForOdds : "";
  let t8;
  if ($[4] !== isLive || $[5] !== t7) {
    t8 = {
      matchId: t7,
      markets: MATCH_PAGE_ODDS_MARKETS as string[],
      isLive,
      pollingIntervalMs: DEFAULT_ODDS_POLLING_INTERVAL_MS,
      enablePolling: isLive
    };
    $[4] = isLive;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const {
    oddsMap,
    loading: oddsLoading,
    refetch: refetchOdds
  } = useMatchOdds(t8);
  const competitorOneData = match.competitorOne;
  const competitorTwoData = match.competitorTwo;
  const isCompetitorOneTeam = competitorOneData.type === "club" || competitorOneData.type === "national";
  let t9;
  if ($[7] !== competitorOneData || $[8] !== isCompetitorOneTeam) {
    t9 = isCompetitorOneTeam ? mergeTeamData(competitorOneData as FUSportsTeamCompetitor, COMPETITORS[competitorOneData.id]) : competitorOneData as FUSportsTeamCompetitor;
    $[7] = competitorOneData;
    $[8] = isCompetitorOneTeam;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const home = t9;
  const isCompetitorTwoTeam = competitorTwoData.type === "club" || competitorTwoData.type === "national";
  let t10;
  if ($[10] !== competitorTwoData || $[11] !== isCompetitorTwoTeam) {
    t10 = isCompetitorTwoTeam ? mergeTeamData(competitorTwoData as FUSportsTeamCompetitor, COMPETITORS[competitorTwoData.id]) : competitorTwoData as FUSportsTeamCompetitor;
    $[10] = competitorTwoData;
    $[11] = isCompetitorTwoTeam;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const away = t10;
  let t11;
  if ($[13] !== away || $[14] !== home) {
    t11 = {
      homeTeam: home,
      awayTeam: away
    };
    $[13] = away;
    $[14] = home;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const {
    homeTeam,
    awayTeam
  } = t11;
  let t12;
  if ($[16] !== awayTeam || $[17] !== awayTeamNativeId || $[18] !== h2hSummary || $[19] !== hasEvents || $[20] !== hasLineups || $[21] !== homeTeam || $[22] !== homeTeamNativeId || $[23] !== isFinished || $[24] !== isLive || $[25] !== liveClock || $[26] !== match || $[27] !== news || $[28] !== oddsLoading || $[29] !== oddsMap || $[30] !== playerIdMapProp || $[31] !== referees || $[32] !== refetchOdds || $[33] !== tvListings || $[34] !== winner) {
    t12 = {
      match,
      homeTeam,
      awayTeam,
      isLive,
      isFinished,
      hasLineups,
      hasEvents,
      oddsMap,
      oddsLoading,
      refetchOdds,
      homeTeamNativeId,
      awayTeamNativeId,
      playerIdMap: playerIdMapProp,
      news,
      tvListings,
      winner,
      h2hSummary,
      referees,
      liveClock
    };
    $[16] = awayTeam;
    $[17] = awayTeamNativeId;
    $[18] = h2hSummary;
    $[19] = hasEvents;
    $[20] = hasLineups;
    $[21] = homeTeam;
    $[22] = homeTeamNativeId;
    $[23] = isFinished;
    $[24] = isLive;
    $[25] = liveClock;
    $[26] = match;
    $[27] = news;
    $[28] = oddsLoading;
    $[29] = oddsMap;
    $[30] = playerIdMapProp;
    $[31] = referees;
    $[32] = refetchOdds;
    $[33] = tvListings;
    $[34] = winner;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const contextValue = t12;
  let t13;
  if ($[36] !== children || $[37] !== contextValue) {
    t13 = <MatchPageDataContext.Provider value={contextValue}>{children}</MatchPageDataContext.Provider>;
    $[36] = children;
    $[37] = contextValue;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}

/**
 * Hook to access match page data in client components.
 * Must be used within a MatchPageDataProvider.
 */
export function useMatchPageData() {
  const context = useContext(MatchPageDataContext);
  if (!context) {
    throw new Error("useMatchPageData must be used within a MatchPageDataProvider");
  }
  return context;
}

/**
 * Hook to optionally access match page data.
 * Returns null if not within a MatchPageDataProvider.
 */
export function useMatchPageDataOptional() {
  return useContext(MatchPageDataContext);
}